import React from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Avatar,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Home as HomeIcon,
  GTranslate as TranslateIcon,
  School as SchoolIcon,
  MenuBook as MenuBookIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import ThemeToggle from '../ThemeToggle';

const Navbar = ({ darkMode, toggleDarkMode }) => {
  const { user, signOut, isAdmin, isPremium } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleClose();
    await signOut();
  };

  const handleUpgradeClick = () => {
    if (location.pathname === '/') {
      const el = document.getElementById('pricing-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    sessionStorage.setItem('scrollToPricing', '1');
    navigate('/');
  };

  return (
    <AppBar
      position="static"
      sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Toolbar>
        <Typography variant="h6" component={RouterLink} to="/" sx={{ 
          flexGrow: 1, 
          textDecoration: 'none',
          color: 'inherit',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
        }}>
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/brand/lexory-icon.png`}
            alt="Lexory"
            sx={{
              height: isMobile ? 28 : 30,
              width: 'auto',
              display: 'block',
              filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.15))',
            }}
          />
          {!isMobile && (
            <Box
              component="span"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.02em',
                lineHeight: 1,
                whiteSpace: 'nowrap',
                textShadow: '0 1px 1px rgba(0,0,0,0.15)',
              }}
            >
              Lexory
            </Box>
          )}
          {/* App title removed - comment out to add */}
        </Typography>
        
        {!isMobile && (
          <ThemeToggle darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
        )}

        {isPremium ? (
          <Box
            sx={{
              ml: isMobile ? 0.5 : 1,
              mr: isMobile ? 0.5 : 1,
              px: isMobile ? 1 : 1.5,
              py: 0.5,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(255,215,0,0.25) 0%, rgba(255,180,0,0.15) 100%)',
              border: '1px solid rgba(255,215,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Typography sx={{ fontSize: isMobile ? '0.85rem' : '0.95rem' }}>💎</Typography>
            <Typography
              sx={{
                fontSize: isMobile ? '0.7rem' : '0.8rem',
                fontWeight: 800,
                color: '#FFD700',
                letterSpacing: 0.5,
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
              }}
            >
              PREMIUM
            </Typography>
          </Box>
        ) : (
          <Button
            color="inherit"
            variant="outlined"
            onClick={handleUpgradeClick}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              ml: isMobile ? 0.5 : 1,
              mr: isMobile ? 0.5 : 1,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              minWidth: isMobile ? 'auto' : '64px',
              px: isMobile ? 1 : 2,
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': { borderColor: 'rgba(255,255,255,0.9)' },
            }}
          >
            {isMobile ? 'Upgrade' : 'Upgrade to Premium'}
          </Button>
        )}
        
        {user ? (
          <>
            {/* Desktop: show nav links inline */}
            {!isMobile && (
              <>
                <Button 
                  color="inherit" 
                  component={RouterLink} 
                  to="/"
                  size="medium"
                  sx={{ mr: 1, fontSize: '0.875rem', minWidth: '64px', px: 2 }}
                >
                  Home
                </Button>
                
                <Button 
                  color="inherit" 
                  component={RouterLink} 
                  to="/saved-translations"
                  size="medium"
                  sx={{ mr: 1, fontSize: '0.875rem', minWidth: '64px', px: 2 }}
                >
                  My Words
                </Button>

                <Button 
                  color="inherit" 
                  component={RouterLink} 
                  to="/flashcards"
                  size="medium"
                  sx={{ mr: 1, fontSize: '0.875rem', minWidth: '64px', px: 2 }}
                >
                  Flashcards
                </Button>
                
                <Button 
                  color="inherit" 
                  component={RouterLink} 
                  to="/library"
                  size="medium"
                  sx={{ mr: 1, fontSize: '0.875rem', minWidth: '64px', px: 2 }}
                >
                  Reading Library
                </Button>
                
                {isAdmin() && (
                  <Button 
                    color="inherit" 
                    component={RouterLink} 
                    to="/admin"
                    size="medium"
                    sx={{ mr: 1, fontSize: '0.875rem', minWidth: '64px', px: 2 }}
                  >
                    Admin Dashboard
                  </Button>
                )}
              </>
            )}
            
            <Box>
              {isMobile ? (
                <Button
                  aria-label="Menu"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                  size="small"
                  endIcon={
                    <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondary.main', fontSize: '0.85rem' }}>
                      {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                  }
                  sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.8rem', minWidth: 'auto', px: 1 }}
                >
                  Menu
                </Button>
              ) : (
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                    {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                  </Avatar>
                </IconButton>
              )}
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                {/* Mobile: nav links inside menu */}
                {isMobile && (
                  <MenuItem component={RouterLink} to="/" onClick={handleClose} sx={{ py: 1.5 }}>
                    <ListItemIcon><HomeIcon fontSize="small" /></ListItemIcon>
                    Home
                  </MenuItem>
                )}
                {isMobile && (
                  <MenuItem component={RouterLink} to="/saved-translations" onClick={handleClose} sx={{ py: 1.5 }}>
                    <ListItemIcon><TranslateIcon fontSize="small" /></ListItemIcon>
                    My Words
                  </MenuItem>
                )}
                {isMobile && (
                  <MenuItem component={RouterLink} to="/flashcards" onClick={handleClose} sx={{ py: 1.5 }}>
                    <ListItemIcon><SchoolIcon fontSize="small" /></ListItemIcon>
                    Flashcards
                  </MenuItem>
                )}
                {isMobile && (
                  <MenuItem component={RouterLink} to="/library" onClick={handleClose} sx={{ py: 1.5 }}>
                    <ListItemIcon><MenuBookIcon fontSize="small" /></ListItemIcon>
                    Reading Library
                  </MenuItem>
                )}
                {isMobile && isAdmin() && (
                  <MenuItem component={RouterLink} to="/admin" onClick={handleClose} sx={{ py: 1.5 }}>
                    <ListItemIcon><AdminIcon fontSize="small" /></ListItemIcon>
                    Admin
                  </MenuItem>
                )}
                {isMobile && (
                  <MenuItem onClick={() => { handleClose(); toggleDarkMode(); }} sx={{ py: 1.5 }}>
                    <ListItemIcon>{darkMode ? '☀️' : '🌙'}</ListItemIcon>
                    {darkMode ? 'Light Mode' : 'Dark Mode'}
                  </MenuItem>
                )}
                {isMobile && <Divider />}
                <MenuItem 
                  component={RouterLink} 
                  to="/profile" 
                  onClick={handleClose}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                  Profile
                </MenuItem>
                <MenuItem onClick={handleSignOut} sx={{ py: 1.5 }}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          </>
        ) : (
          <>
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/"
              size={isMobile ? "small" : "medium"}
              sx={{ 
                mr: isMobile ? 0.5 : 1,
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                minWidth: isMobile ? 'auto' : '64px',
                px: isMobile ? 1 : 2
              }}
            >
              Home
            </Button>
            {!isMobile && (
              <Button 
                color="inherit" 
                component={RouterLink} 
                to="/saved-translations"
                size="medium"
                sx={{ 
                  mr: 1,
                  fontSize: '0.875rem',
                  minWidth: '64px',
                  px: 2
                }}
              >
                My Words
              </Button>
            )}
            {!isMobile && (
              <Button 
                color="inherit" 
                component={RouterLink} 
                to="/login"
                size="medium"
                sx={{ 
                  mr: 1,
                  fontSize: '0.875rem',
                  minWidth: '64px',
                  px: 2
                }}
              >
                Flashcards
              </Button>
            )}
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/login"
              size={isMobile ? "small" : "medium"}
              sx={{ 
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                minWidth: isMobile ? 'auto' : '64px',
                px: isMobile ? 1 : 2
              }}
            >
              Login
            </Button>
            {isMobile && (
              <ThemeToggle darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
            )}
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
